import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { FeatureFlagView } from "@/lib/api/types";
import { endpoints } from "@/lib/api/endpoints";
import { apiJsonClient } from "@/lib/query/core/api-json-client";
import {
  invalidateAdminFeatureFlagAudit,
  invalidateAdminFeatureFlags,
} from "@/lib/query/core/invalidation";

// Every field optional — a PATCH may carry only isActive (the row toggle), only
// the targeting, or the full metadata edit. Omitting storeIds leaves the
// allow-list untouched; sending [] clears it.
export type UpdateFeatureFlagInput = {
  id: string;
  nameEn?: string;
  nameAr?: string;
  descriptionEn?: string;
  descriptionAr?: string;
  isActive?: boolean;
  enableForAllStores?: boolean;
  storeIds?: string[];
};

export function useUpdateFeatureFlagMutation() {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: ({ id, ...body }: UpdateFeatureFlagInput) =>
      apiJsonClient<FeatureFlagView>(endpoints.admin.featureFlagById(id), {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: async (_data, variables) => {
      await invalidateAdminFeatureFlags(queryClient);
      await invalidateAdminFeatureFlagAudit(queryClient, variables.id);
    },
  });

  return {
    mutateAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
    error: mutation.error,
    reset: mutation.reset,
  };
}
